import { Feature } from '@/pinia/feature'
import { RGBA, colorBetween } from '@/utils/rgba'

const createColorScale = (color: string, from: number, to: number) => ({
  type: 'INTERPOLATE',
  cutoffs: [
    {
      value: from,
      color: colorBetween(RGBA.fromHex('#ffffff'), RGBA.fromHex(color), 0.1).toHex()
    },
    { value: to * 0.5, color: color },
    { value: to, color: colorBetween(RGBA.fromHex('#000000'), RGBA.fromHex(color), 0.6).toHex() }
  ]
})

const numberColor = '#40A9FF'
const areaColor = '#F759AB'

export const apartments: Feature = {
  name: 'apartments',
  enabled: true,
  title: {
    en: 'Apartments',
    sv: 'Lägenheter'
  },
  isRoute: true,
  access: ['FEAT_PORTFOLIO-ANALYSIS-APARTMENTS_READER'],
  component: 'DashboardDataSectionFlat',
  props: {
    enabledEntityLevels: ['REGION', 'COMPLEX', 'REAL_ESTATE', 'CONSTRUCTION'],
    dataSources: [
      {
        id: 'portfolio.measurement.apartments.number.value',
        type: 'fetch',
        fetch: 'fetch.portfolio.apartments.measurements',
        path: 'measurements[0].splits[0].value'
      },
      {
        id: 'portfolio.measurement.apartments.area.value',
        type: 'fetch',
        fetch: 'fetch.portfolio.apartments.measurements',
        path: 'measurements[1].splits[0].value'
      },
      {
        id: 'portfolio.measurement.apartments.number.colored-entities',
        type: 'fetch',
        fetch: 'fetch.portfolio.apartments.measurements',
        transform: {
          id: 'portfolio.apartments.colored-entities',
          index: 2
        }
      },
      {
        id: 'portfolio.measurement.apartments.area.colored-entities',
        type: 'fetch',
        fetch: 'fetch.portfolio.apartments.measurements',
        transform: {
          id: 'portfolio.apartments.colored-entities',
          index: 3
        }
      },
      {
        id: 'portfolio.histogram.apartments.number',
        type: 'fetch',
        fetch: 'fetch.portfolio.apartments.histograms',
        transform: {
          id: 'portfolio.apartments.histogram',
          index: 0
        }
      },
      {
        id: 'portfolio.histogram.apartments.category',
        type: 'fetch',
        fetch: 'fetch.portfolio.apartments.histograms',
        transform: {
          id: 'portfolio.apartments.histogram',
          index: 1
        }
      }
    ],
    informationFetch: 'fetch.portfolio.apartments.information'
  },
  children: [
    {
      name: 'number',
      title: {
        sv: 'Antal lägenheter',
        en: 'Number of apartments'
      },
      info: {
        sv: 'Antal registrerade lägenheter baserat på myndighetsdata, adderas för urvalet.',
        en: 'Number of registered apartments based on authority data, summed for the selection.'
      },
      enabled: true,
      component: 'MainMeasurementCard',
      props: {
        initialShowOnMap: true,
        value: {
          dataSource: 'portfolio.measurement.apartments.number.value'
        },
        coloredEntities: {
          dataSource: 'portfolio.measurement.apartments.number.colored-entities'
        },
        colorLegend: {
          hideMean: true
        },
        colorScale: createColorScale(numberColor, 0, 50),
        color: numberColor
      }
    },
    {
      name: 'boa',
      title: {
        sv: 'Boarea (BOA)',
        en: 'Living area (BOA)'
      },
      info: {
        sv: 'Boarean utgörs av utrymmen inrättade för boende, och redovisas per byggnad, aggregerat från angivna värden för individuella lägenheter. Summeringen baseras på urvalet.',
        en: 'The residential floor area consists of spaces set up for living purposes and is disclosed per building, aggregated from specified values for individual apartments. The summary is based on the current selection.'
      },
      enabled: true,
      component: 'MainMeasurementCard',
      props: {
        initialShowOnMap: false,
        value: {
          dataSource: 'portfolio.measurement.apartments.area.value',
          format: {
            format: {
              type: 'intl-number',
              maximumFractionDigits: 0
            },
            steps: [{ format: { type: 'format-string', format: '%s m²' } }]
          },
          isLoading: false
        },
        coloredEntities: {
          dataSource: 'portfolio.measurement.apartments.area.colored-entities'
        },
        colorScale: createColorScale(areaColor, 0, 2000),
        colorLegend: {
          hideMean: true
        },
        color: areaColor
      }
    },
    {
      name: 'hist-room',
      enabled: true,
      title: {
        sv: 'Lägenhetsspridning - rum',
        en: 'Apartment distribution - rooms'
      },
      info: {
        en: 'Number of apartments for each apartment size is added, based on the current selection.',
        sv: 'Antal lägenheter för varje lägenhetsstorlek adderas, baserat på det urval som gjorts.'
      },
      component: 'CardChart',
      props: {
        enableFullscreen: true,
        chart: {
          type: 'histogram',
          codomainFormat: {
            type: 'intl-number',
            style: 'percent',
            maximumFractionDigits: 1
          },
          domainFormat: {
            format: {
              type: 'intl-number',
              maximumFractionDigits: 0
            },
            steps: [
              { format: { type: 'locale-format-string', format: { sv: '%s RoK', en: '%s rooms' } } }
            ]
          },
          valueFilterTreshold: 0.001,
          missingDataMessage: {
            en: 'Missing data',
            sv: 'Saknar data'
          },
          label: {
            sv: 'Antal lägenheter',
            en: 'Number of apartments'
          },
          color: '#40A9FF',
          dataSource: 'portfolio.histogram.apartments.number'
        }
      }
    },
    {
      name: 'hist-category',
      enabled: true,
      title: {
        sv: 'Lägenhetsspridning - kategori',
        en: 'Apartment distribution - category'
      },
      info: {
        en: 'Number of apartments for each apartment type is added, based on the current selection.',
        sv: 'Antal lägenheter av varje lägenhetstyp adderas, baserat på det urval som gjorts.'
      },
      component: 'CardChart',
      props: {
        enableFullscreen: true,
        chart: {
          type: 'histogram',
          codomainFormat: {
            format: {
              type: 'intl-number',
              maximumFractionDigits: 0
            }
          },
          domainFormat: {
            format: {
              type: 'locale-mapping',
              mapping: {
                0: { en: 'Regular', sv: 'Vanlig' },
                1: { en: 'Special needs', sv: 'Särskilt behov' },
                2: { en: 'Student', sv: 'Student' },
                3: { en: 'Misc, special', sv: 'Övrig, special' },
                4: { en: 'Unknown', sv: 'Okänd' }
              }
            }
          },
          valueFilterTreshold: 0.001,
          missingDataMessage: {
            en: 'Missing data',
            sv: 'Saknar data'
          },
          label: {
            sv: 'Antal lägenheter',
            en: 'Number of apartments'
          },
          color: '#40A9FF',
          dataSource: 'portfolio.histogram.apartments.category'
        }
      }
    }
  ]
}
